/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { getCatalogId, IComponentProps, ICoreContext, Image, getUrlSync, IImageSettings, getAsset } from '@msdyn365-commerce/core';
import { ImageFetchPriority } from '@msdyn365-commerce/core-internal';
import {
    getProductPageUrlSync, ProductInput,
    GetCustomerInput, getCustomer, buildWishlistLine, getWishlistByCustomerId,
    OrderTemplate
} from '@msdyn365-commerce-modules/retail-actions';
import { getCartStateData, GetCartStateDataInput, ICartState } from '@msdyn365-commerce/global-state';
import { format, Tooltip, UncontrolledTooltip } from '@msdyn365-commerce-modules/utilities';
import React from 'react';
import { reaction } from 'mobx';
import { Button, ButtonProps } from 'reactstrap';
import classnames from 'classnames';
import debounce from 'lodash/debounce';
import { toast, IToastViewProps, toasts } from '../toast/toast';
import { ProductListProduct, IActionsAvailable, IProductListResources, BuyValidationResult } from './product-list-component';
import { AsyncResult, CommerceList, Customer, PeriodicDiscountOfferType, ProductList, ProductPrice, SimpleProduct, UnitOfMeasureConversion } from '@msdyn365-commerce/retail-proxy';
import { addLinesAsync, createCommerceListAsync, createGetByCustomerInput, removeLinesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CommerceListsDataActions.g';
import { getLocale } from './locale';
import {
    generateAddToOrderTemplateDialogResources,
    generateOrderTemplateNameDialogResources,
    generateItemAddedToOrderTemplateDialogResources,
    generateDuplicateItemsWhenAddingToOrderTemplateDialogResources
} from './locale/add-to-order-template-resources';
import AddToOrderTemplateComponent from '../../themes/dlvry-adventureworks/views/components/addtoordertemplate.component';
import { validateAlcoholLicenseAsync } from '../../actions/validate-alcohol-license.action';
import PQueue from 'p-queue';
import PriceComponent from '../../themes/dlvry-adventureworks/views/components/price.component';
import { humanizeUnitOfMeasure, UnitOfMeasure } from '../unit-of-measure';
import { getConversionText, getKilogramConversion, getNonBreakoffUOM, getPcsConversion, getRoundedFactor, getTotalConversionText } from '../../shared-utils/unit-conversion-utils';
import DiscountBadge from '../discount-badge/discount-badge';
import { ICartViewProps } from '../../modules/dlvry-cart/dlvry-cart';
import { getDefaultBuyQuantity } from '../../shared-utils/common-utils';
import { getTemperatureZoneIcon } from '../../shared-utils/attributes/get-temperature-zone';
import { multiplyPriceBy } from '../../shared-utils/price-utils';
import QuantityInput from '../common/quantity-input';
import QuantityBreakoffSelector from '../common/quantity-breakoff-selector';
import { IProductAgreement, IProductError } from '../../shared-utils/types';
import { IProductListByProductId } from '../../actions/get-product-lists-by-product-ids';
import { ensureBaseImageUrl } from '../../shared-utils/image-utilities';
import { DebouncedFunc } from 'lodash';
import UnitConversionDetails from '../UnitConversionDetails';
import { IBrandSchemeEntity } from '../../actions/entities/brand-scheme-entity';
import * as GTM from '../../shared-utils/google-tag-manager/gtm';
import { LocalDropdown } from '../dropdown/dropdown';
import { AlternativeProducts } from '../dropdown/alternative-products';
import { IProductRelationTypeForProduct } from '../../actions/get-relation-types-by-ids';
import { ProductClassification } from '../../shared-utils/attributes';
import { BulbYellow } from '../icons/bulb-yellow';
import { IAppSettingsInContext } from '../../settings/app.settings.autogenerated';

/**
 * Data required to render each order history row.
 */
export interface IProductListRowData {
    cart?: ICartState;
    product: ProductListProduct;
    simpleProduct: SimpleProduct | undefined;
    orderTemplate?: OrderTemplate;
    orderTemplates?: OrderTemplate[];
    customerInformation?: Customer;
    customerAgreements?: IProductAgreement[];
    unitConversions?: UnitOfMeasureConversion[];
    wishlists?: CommerceList[];
    brandSchemes?: IBrandSchemeEntity[];
}

/**
 * Configuration for the order history rows.
 */
export interface IProductListRowProps extends IComponentProps<IProductListRowData> {
    context: ICoreContext<IAppSettingsInContext>;
    className: string;
    cartQueue: PQueue;
    actionsAvailable?: IActionsAvailable;
    resources: IProductListResources;
    cartViewProps?: ICartViewProps | undefined;
    defaultWishlistName?: string;
    productComparisonButton?: {} | null | undefined;
    moduleId: string;
    moduleType: string;
    error?: IProductError;
    allowMultiSelect?: boolean;
    allowZeroQuantity?: boolean;
    min?: number;
    isDragOver?: 'over' | 'under' | undefined;
    isSelected?: boolean;
    readOnly?: boolean;
    disableBuyButton?: boolean;
    disableReorder?: boolean;
    disableQuantityInput?: boolean;
    draggable?: boolean;
    historicalPrice?: boolean;
    imageSettings?: IImageSettings;
    showAvailability?: boolean;
    showPrices?: boolean;
    showTotalPrices?: boolean;
    showCartCount?: boolean;
    userHasAccess?: boolean;
    selectOnQuantityChange?: boolean;
    initialBreakoffEnabled?: boolean;
    buyAlwaysAdd?: boolean;
    resetSelectedQuantities?: boolean;
    smallerImage?: boolean;
    smallestImage?: boolean;
    relationTypesByIds?: IProductRelationTypeForProduct[];
    retrieveOrderTemplates?(): Promise<OrderTemplate[]>;
    onProductRemove?: (product: ProductListProduct) => Promise<void>;
    onSelectCartHandler?: () => void;
    onUpdateOrderTemplates?: (product?: ProductListProduct, orderTemplate?: ProductList) => Promise<OrderTemplate[]>;
    onUpdateQuantity?: (quantity: number) => void;
    onBreakoffChange?: (product: ProductListProduct, enabled: boolean) => void;
    onDrop?: (event: React.DragEvent<HTMLLIElement>, recordId: number | undefined, overUnder: 'over' | 'under' | undefined) => void;
    onDragOver?: (recordId: number | undefined, overUnder: 'over' | 'under' | undefined) => void;
    onBuyValidate?: (product: ProductListProduct, quantity: number) => Promise<BuyValidationResult>;
    onChangeProduct?: (product: ProductListProduct, replacement: ProductListProduct, recommendationType: string, position: number) => void;
}

export interface IProductListRowState {
    orderQuantity: number;
    working: boolean;
    showCartCountTooltip: boolean;
    imageHovered: boolean;
    showUnitConversionDetails: boolean;
    revealAlternativeProductsInfo: boolean;
}

/**
 * Renders rows for desktop view.
 * @param props - Configuration for the rows.
 * @returns A react node with a list of order history rows for desktop view.
 */
export class ProductListRow extends React.Component<IProductListRowProps, IProductListRowState> {

    private readonly locale = getLocale(this.props.context);
    private readonly cartUrl = getUrlSync('cart', this.props.context.actionContext);

    private readonly onImageHover: DebouncedFunc<() => void>;
    private readonly onImageUnhover: DebouncedFunc<() => void>;

    public constructor(props: IProductListRowProps) {
        super(props);
        this.state = {
            orderQuantity: props.buyAlwaysAdd
                ? 0
                : props.data.product.CurrentQuantity ?? 0,
            working: false,
            showCartCountTooltip: false,
            imageHovered: false,
            showUnitConversionDetails: false,
            revealAlternativeProductsInfo: true
        };

        this.onImageHover = debounce(() => this.setState({ imageHovered: true }), 400);
        this.onImageUnhover = debounce(() => this.setState({ imageHovered: false }), 800);
    }

    public componentDidMount(): void {
        reaction(
            () => this.props.data.cart?.cart,
            () => {
                // Force update to fix cart count lagging behind
                this.forceUpdate();
            }
        );

        if (this.props.buyAlwaysAdd) {
            return;
        }

        reaction(
            () => this.props.data.cart?.cart.CartLines,
            () => {
                const { cart, product } = this.props.data;

                if ((product.CartLine?.Quantity ?? 0) > 0 && !cart?.cart.CartLines?.find(x => x.ProductId === product?.RecordId)) {
                    // This product was removed from cart, reset quantity
                    this.setState({
                        orderQuantity: 0
                    });
                }
            }
        );
    }

    private renderProductComparisonButton(
        productComparisonButton: React.ReactNode,
        product: any,
        catalogId: number
    ): JSX.Element | undefined {
        return React.cloneElement(productComparisonButton as React.ReactElement, { product, catalogId });
    }

    public componentDidUpdate(prevProps: Readonly<IProductListRowProps>, prevState: Readonly<IProductListRowState>, snapshot?: any): void {
        if (this.props.buyAlwaysAdd) {
            if (this.props.isSelected && this.props.resetSelectedQuantities) {
                this.setState({ orderQuantity: 0 });
            }
            return;
        }

        if (this.props.data.product.CurrentQuantity !== prevProps.data.product.CurrentQuantity) {
            this.setState({
                orderQuantity: this.props.data.product.CurrentQuantity ?? this.state.orderQuantity
            });
        }
    }

    public render(): JSX.Element | undefined {
        const {
            className,
            context,
            data: { product, brandSchemes, customerAgreements },
            actionsAvailable,
            allowMultiSelect,
            draggable,
            readOnly,
            showPrices
        } = this.props;
        const { PrimaryImageUrl } = product;
        const productUrl = product ? getProductPageUrlSync(product.Name ?? '', product.RecordId, context.actionContext, undefined) : '';
        const isAuthenticated = context.request.user.isAuthenticated;
        const price = showPrices !== false
            ? (product.Price && product.Price !== 0.00)
                ? product.Price : undefined
            : undefined;

        const conversions = this.props.data.unitConversions?.filter(x => x.FromUnitOfMeasureSymbol === this.props.data.product?.DefaultUnitOfMeasure);
        const kilogramConversion = conversions && getKilogramConversion(this.props.data.product?.DefaultUnitOfMeasure, conversions);
        const pcsConversion = conversions && getPcsConversion(conversions);
        const temperatureZoneIcon = product.TemperatureZone ? getTemperatureZoneIcon(context, product.TemperatureZone) : undefined;
        const totalConversionText = getTotalConversionText(
            context,
            this.props.data.simpleProduct,
            this.props.data.unitConversions,
            this.state.orderQuantity,
            !!this.props.initialBreakoffEnabled
        );
        const additionalInformation = (product.AlcoholProduct && product.AlcoholProduct > 0 && product.ImporterGrouping) && (
            <div className={`${className}__product__importer-grouping`}>{product.ImporterGrouping}</div>
        ) || product.Brand && (
            <div className={`${className}__product__brand`}>{product.Brand}</div>
        ) || product.Manufacturer && (
            <div className={`${className}__product__manufacturer`}>{product.Manufacturer}</div>
        ) || product.Producer && (
            <div className={`${className}__product__producer`}>{product.Producer}</div>
        );
        const unitConversionDetailsEnabled = (context.app as any).config?.enableUnitConversionDetails;
        const allowedOUNsForAlternativeProducts = context.app.config?.alternativeProductsAllowedOUNs?.split(',').map(oun => oun.trim().toUpperCase()) || [];
        const showAlternativeProducts = context.app.config?.enableAlternativeProductsFeature &&
            (allowedOUNsForAlternativeProducts.length === 0 || allowedOUNsForAlternativeProducts.includes(context.request.apiSettings.oun.toUpperCase())) && // Alternative products only for allowed OUNs, if any are configured
            actionsAvailable?.addToCartOrderTemplate &&
            !!this.props.relationTypesByIds?.some(x => x.ProductId === this.props.data.product.RecordId) &&
            (product.Classification !== ProductClassification.A || (product.Classification === ProductClassification.A && !product.Availability?.IsProductAvailable));

        return (
            <li
                key={`${product.RecordId}-${product.DefaultUnitOfMeasure}`}
                data-product-id={product.RecordId}
                className={classnames({
                    'drag-over': this.props.isDragOver,
                    [this.props.isDragOver || '']: this.props.isDragOver
                })}
                onDrop={(e) => {
                    e.stopPropagation(); // Prevent onDrop handler on parent from firing
                    this.props.onDrop?.(e, product.RecordId, this.props.isDragOver);
                }}
                onDragOver={(e) => {
                    e.stopPropagation();

                    if (!this.props.disableReorder && e.dataTransfer.types.includes('product')) {
                        e.preventDefault();

                        const rect = (e.currentTarget as Element).getBoundingClientRect();
                        const y = e.clientY - rect.top;
                        const halfHeight = rect.height / 2;
                        const overOrUnder: 'over' | 'under' = y > halfHeight ? 'under' : 'over';

                        this.props.onDragOver?.(product.RecordId, overOrUnder);
                    }
                }}
            >
                {draggable && (
                    <div
                        className='drag-area'
                        draggable={draggable}
                        onDragStart={draggable ? this._handleDragStart : undefined}
                        onDragEnd={() => this.props.onDragOver?.(undefined, undefined)}
                        onDragOver={draggable ? (e) => {
                            e.stopPropagation();
                            e.preventDefault();
                        } : undefined}
                    >
                        <img className='drag-area__icon' src={getAsset('icons/drag-handle.svg', context.request)} />
                    </div>
                )}
                {isAuthenticated && allowMultiSelect && (
                    <label className='checkbox-container'>
                        <input
                            aria-label='selectOneRowText'
                            className='checkbox-input'
                            type='checkbox'
                            checked={this.props.isSelected}
                            aria-checked={this.props.isSelected}
                            onChange={this.props.onSelectCartHandler}
                        />
                        <span className='checkmark'/>
                    </label>
                )}
                <div className={`${className}__product-image`}>
                    <div
                        className={classnames(`${className}__image-action-container`, {
                            small: this.props.smallerImage,
                            smallest: this.props.smallestImage
                        })}
                    >
                        <a
                            className={`${className}__image-action-container__product-image`}
                            href={productUrl}
                            onMouseEnter={() => {
                                this.onImageUnhover.cancel();
                                this.onImageHover();
                            }}
                            onMouseLeave={() => {
                                this.onImageHover.cancel();
                                this.onImageUnhover();
                            }}
                            style={{ zIndex: /* this.state.imageHovered ? 9998 : */ undefined }}
                        >
                            {this._renderProductImage(
                                'product-image',
                                context,
                                product.Name || '',
                                PrimaryImageUrl,
                                PrimaryImageUrl,
                                this.props.imageSettings
                            )}
                            {product.Badge && (
                                <DiscountBadge
                                    context={context}
                                    badge={product.Badge}
                                    size='medium'
                                    sizeMultiplier={2} />
                            )}
                        </a>
                        {isAuthenticated && (
                            <div className={`${className}__image-action-container__actions`}>
                                {this.props.retrieveOrderTemplates && (
                                    <AddToOrderTemplateComponent
                                        className='order-template-button'
                                        addToOrderTemplateButtonText={this.locale.addToOrderTemplate.addToOrderTemplateButtonText || 'addToOrderTemplateButtonText'}
                                        addToOrderTemplateButtonTooltip={this.locale.addToOrderTemplate.addToOrderTemplateButtonTooltip || 'addToOrderTemplateButtonTooltip'}
                                        addToOrderTemplateDialogResources={generateAddToOrderTemplateDialogResources(this.locale)}
                                        createOrderTemplateDialogResources={generateOrderTemplateNameDialogResources(this.locale)}
                                        itemAddedToOrderTemplateDialogResources={generateItemAddedToOrderTemplateDialogResources(this.locale)}
                                        duplicateItemsWhenAddingToOrderTemplateDialogResources={generateDuplicateItemsWhenAddingToOrderTemplateDialogResources(this.locale)}
                                        context={context}
                                        id={this.props.moduleId}
                                        typeName={this.props.moduleType}
                                        disabled={!this.props.data.simpleProduct || !this.props.data.customerInformation}
                                        disableButton={!this.props.data.simpleProduct || !this.props.data.customerInformation}
                                        data={{
                                            orderTemplates: this.props.data.orderTemplates,
                                            product: this.props.data.simpleProduct || {} as any,
                                            quantity: this.state.orderQuantity || 1,
                                            customerInformation: this.props.data.customerInformation || { IsB2b: true } as any, // Always pass 'IsB2b' so that it will always render (unless false from api)
                                            partOfProductLists: this.props.data.product.PartOfProductLists
                                        }}
                                        onSuccess={async (orderTemplate) => {
                                            await this.props.onUpdateOrderTemplates?.(this.props.data.product, orderTemplate);
                                            toast({
                                                text: `${this.props.data.product.Name}${this.locale.addToOrderTemplate.itemAddedToOrderTemplateHeaderMessageText}`,
                                                autoClose: 3000,
                                                canClose: true,
                                                position: 'bottom-right',
                                                type: 'success',
                                                showProgress: true
                                            });
                                        }}
                                        retrieveOrderTemplates={this.props.retrieveOrderTemplates}
                                    />
                                )}
                                {actionsAvailable?.addToWishlistAction && this._getWishlistActionButton()}
                            </div>
                        )}
                    </div>
                </div>

                <div className={`${className}__product-wrapper`}>
                    <div className={`${className}__product`}>
                        {product.Category && (
                            <div className={`${className}__product__category`}>{product.Category}</div>
                        )}
                        {product.Producer && (
                            <div className={`${className}__product__producer`}>{product.Producer}</div>
                        )}
                        <div className={`${className}__product__name-wrapper`}>
                            <a className={`${className}__product__name`} href={productUrl}>
                                {product.Name && this.props.error && (
                                    <span>
                                        <i
                                            className={`${className}__product__${this.props.error.Type}`}
                                            title={this.props.error.Error} />
                                        <span className='sr-only'>{this.props.error.Error}</span>
                                    </span>
                                )}
                                {product.Transit && (
                                    <div className={`${className}__product__transit-mark`} title={this.props.resources.transitTooltip || 'Transittvare'}>T</div>
                                )}
                                {customerAgreements?.some(x => product.Agreements?.find(y => y.AttributeRecordId === x.AttributeRecordId)) && (
                                    <div className={`${className}__product__agreement-product`} title={this.props.resources.agreementProductTooltip || 'Avtalevare'}>A</div>
                                )}
                                {temperatureZoneIcon && (
                                    <span
                                        className={`${className}__product__temperature-icon ${temperatureZoneIcon}`}
                                        title={product.TemperatureZone} />
                                )}
                                <span>{product.Name}</span>
                            </a>
                        </div>
                        {this.props.data.unitConversions && (
                            <div
                                className={classnames(`${className}__product__unit-conversion`, { enabled: unitConversionDetailsEnabled })}
                                onClick={() => this.setState({ showUnitConversionDetails: true })}
                            >
                                {getConversionText(
                                    context,
                                    product.DefaultUnitOfMeasure,
                                    this.props.data.unitConversions,
                                    this.props.data.simpleProduct,
                                    !!this.props.initialBreakoffEnabled
                                )}
                                <UnitConversionDetails
                                    context={context}
                                    enabled={unitConversionDetailsEnabled}
                                    isOpen={this.state.showUnitConversionDetails}
                                    toggle={() => this.setState({ showUnitConversionDetails: !this.state.showUnitConversionDetails })}
                                    productName={product.Name}
                                    productUOM={product.DefaultUnitOfMeasure}
                                    unitConversions={this.props.data.unitConversions || []}
                                    simpleProduct={this.props.data.simpleProduct}
                                    breakoff={this.props.initialBreakoffEnabled}
                                />
                            </div>
                        )}
                        <div className={`${className}__product__id`}>
                            {product.ItemId}
                        </div>
                        {additionalInformation}
                        {this._renderAvailability()}
                    </div>
                    {!isAuthenticated && (
                        <div className={`${className}__price__not-logged-in`}>
                            {this.props.resources.priceNotLoggedInText || 'Logg inn for å se priser'}
                        </div>
                    )}
                </div>

                {isAuthenticated && (
                    <div className={classnames(`${className}__right`, { 'read-only': readOnly })}>
                        <div className={classnames(`${className}__price-and-indicators`, { 'add-margin': actionsAvailable?.removeAction })}>
                            <div className={`${className}__prices-wrapper`}>
                                <div className={`${className}__price`}>
                                    {this._renderPrice(className, price)}
                                </div>

                                {price && this.props.historicalPrice && (
                                    <div className={`${className}__price__extra`}>
                                        <PriceComponent
                                            context={context}
                                            data={{ price: { CustomerContextualPrice: price / this.state.orderQuantity } }}
                                            id={this.props.id}
                                            typeName={this.props.typeName} />
                                        &nbsp;
                                        pr. {humanizeUnitOfMeasure(context, product?.DefaultUnitOfMeasure).toLowerCase()}
                                    </div>
                                )}

                                {price && !this.props.historicalPrice && !!(!this.props.initialBreakoffEnabled && this.props.data.simpleProduct?.Behavior?.MultipleOfQuantity && this.props.data.simpleProduct.Behavior.MultipleOfQuantity > 1) && (
                                    <div className={`${className}__price__extra`}>
                                        <PriceComponent
                                            context={context}
                                            data={{ price: { CustomerContextualPrice: price } }}
                                            id={this.props.id}
                                            typeName={this.props.typeName} />
                                        &nbsp;
                                        pr. {humanizeUnitOfMeasure(context, product?.DefaultUnitOfMeasure).toLowerCase()}
                                    </div>
                                )}

                                {price && kilogramConversion && getRoundedFactor(kilogramConversion) > 0 && (
                                    <div className={`${className}__price__extra`}>
                                        <PriceComponent
                                            context={context}
                                            data={{ price: { CustomerContextualPrice: price / getRoundedFactor(kilogramConversion) } }}
                                            id={this.props.id}
                                            typeName={this.props.typeName} />
                                        &nbsp;
                                        pr. kg
                                    </div>
                                )}

                                {price && pcsConversion && (
                                    <div className={`${className}__price__extra`}>
                                        <PriceComponent
                                                context={context}
                                                data={{ price: { CustomerContextualPrice: price / (pcsConversion.Factor || 1) } }}
                                                id={this.props.id}
                                                typeName={this.props.typeName} />
                                        &nbsp;
                                        pr. stk
                                    </div>
                                )}

                                {this._renderDepositPrice(price)}
                            </div>

                            <div className={`${className}__badge-wrapper`}>
                                {this._renderOrderTemplateIndicator(className)}
                                {this.props.showCartCount && this._renderCartCount(className)}
                            </div>
                        </div>

                        {this.props.readOnly && (
                            <div className={`${className}__quantity-wrapper`}>
                                <span style={{ fontSize: 14 }}>
                                    {this.state.orderQuantity} {humanizeUnitOfMeasure(context, product.DefaultUnitOfMeasure, this.props.data.simpleProduct).toLocaleLowerCase()}
                                </span>
                            </div>
                        )}

                        {!this.props.readOnly && (
                            <div className={`${className}__quantity-wrapper`}>
                                {this._getSearchResultOrderQuantity(readOnly)}
                                <QuantityInput
                                    context={context}
                                    disable={this.props.disableQuantityInput}
                                    product={this.props.data.simpleProduct}
                                    quantity={this.state.orderQuantity}
                                    breakoff={!!this.props.initialBreakoffEnabled}
                                    min={this.props.min}
                                    onQuantityChange={this._onUpdateQuantity}
                                    showNonBreakoffAmount={true}
                                    nonBreakoffUOM={getNonBreakoffUOM(this.props.data.simpleProduct, this.props.data.unitConversions)}
                                    allowZero={this.props.allowZeroQuantity}
                                />
                                {totalConversionText && (
                                    <span className={`${className}__total-conversion-text`}>
                                        =
                                    </span>
                                )}
                                <span className={`${className}__total-conversion-text`} style={{ minWidth: 60 }}>
                                    {totalConversionText}
                                </span>
                            </div>
                        )}

                        {this.props.showTotalPrices &&
                            this._renderTotalPrice(className, price)
                        }

                        {actionsAvailable && (
                            <div className={`${className}__actions`}>
                                {/* {context.app.config?.enableAlternativeProductsFeature && actionsAvailable?.addToCartOrderTemplate && (
                                    <Button
                                        className={`${className}__change-product-button`}
                                        title='Bytt'
                                        onClick={() => this.props.onChangeProduct?.(product)}
                                    >
                                        Bytt
                                    </Button>
                                )} */}
                                {showAlternativeProducts && (
                                    <>
                                        <div className={classnames(`${className}__change-product-information__wrapper`, { reveal: false })}>
                                            <div className={`${className}__change-product-information__text`}>
                                                Se våre anbefalte alternativer!
                                            </div>
                                            <button className={`${className}__change-product-information__icon`}>
                                                <BulbYellow height={24} width={24} />
                                            </button>
                                        </div>
                                        <LocalDropdown
                                            trigger={({ ref, onClick, 'aria-expanded': ariaExpanded }) => (
                                                <button
                                                    ref={ref as any}
                                                    className={`${className}__change-product-button`}
                                                    onClick={() => {
                                                        void GTM.beginAltProductReplaceedAsync(product);
                                                        onClick();
                                                    }}
                                                    aria-expanded={ariaExpanded}
                                                >
                                                    {this.props.resources.alternativeProductsButtonText}
                                                </button>
                                            )}
                                        >
                                            {(close) => (
                                                <AlternativeProducts
                                                    id={this.props.id}
                                                    typeName={this.props.typeName}
                                                    context={context}
                                                    resources={{
                                                        alternativeProductsChangeProductButtonText: this.props.resources.alternativeProductsChangeProductButtonText!,
                                                        alternativeProductsLoadingText: this.props.resources.alternativeProductsLoadingText!,
                                                        alternativeProductsNoResultsText: this.props.resources.alternativeProductsNoResultsText!,
                                                        alternativeProductsErrorText: this.props.resources.alternativeProductsErrorText!
                                                    }}
                                                    product={this.props.data.product}
                                                    relationTypesByIds={this.props.relationTypesByIds}
                                                    onChangeProduct={this.props.onChangeProduct}
                                                />
                                            )}
                                        </LocalDropdown>
                                    </>
                                )}
                                {actionsAvailable?.addToCartAction && (
                                    this._getCartButton()
                                )}
                                {actionsAvailable?.addToCartOrderTemplate && (
                                    this._getOrderTemplateBuyButton()
                                )}
                                {actionsAvailable?.productComparisonButton && this.props.productComparisonButton && this.renderProductComparisonButton(
                                    this.props.productComparisonButton,
                                    product,
                                    getCatalogId(context.request))}
                                {/* {context.app.config?.enableAlternativeProductsFeature && actionsAvailable?.addToCartOrderTemplate && (
                                    <Button
                                        className={`${className}__change-product-button`}
                                        title='Bytt'
                                        onClick={() => this.props.onChangeProduct?.(product)}
                                    />
                                )} */}
                            </div>
                        )}
                    </div>
                )}

                {isAuthenticated && ((actionsAvailable && Object.values(actionsAvailable).some(Boolean)) || draggable) && (
                    <div className={`${className}__line-actions`}>
                        {actionsAvailable?.removeAction && this._getRemoveActionButton(
                            'btn-remove',
                            this.props.resources.removeButtonText || 'Fjern',
                            this._removeItem)}
                    </div>
                )}

                {product.SpecialBadges && (
                    <div className={`${className}__special-badges`} style={{ zIndex: /* this.state.imageHovered ? 9999 : */ undefined }}>
                        {product.SpecialBadges.split('|').map((badge: string, index: number) => {
                            const brandScheme = brandSchemes?.find(x => x.BrandSchemeId === badge);

                            return (
                                <img
                                    key={`${product.RecordId}-${brandScheme?.BrandSchemeId}`}
                                    src={brandScheme?.ImageUrl}
                                    alt={brandScheme?.BrandSchemeName}
                                    title={brandScheme?.BrandSchemeName} />
                            );
                        })}
                    </div>
                )}
            </li>
        );
    }

    private readonly _handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
        e.stopPropagation();

        const { product } = this.props.data;
        const offsetX = e.clientX - (e.target as Element).getBoundingClientRect().left;
        const offsetY = e.clientY - (e.target as Element).getBoundingClientRect().top;

        e.dataTransfer.setData('product', JSON.stringify({
            ProductName: product.Name || '',
            ProductRecordId: product.RecordId || 0,
            ProductUnitOfMeasure: product.DefaultUnitOfMeasure || ''
        }));
        e.dataTransfer.setDragImage((e.target as Element).parentElement as Element, offsetX, offsetY);
        e.dataTransfer.effectAllowed = 'copyMove';
    };

    private _renderPrice = (className: string, price: number | undefined): React.ReactNode => {
        const {
            context,
            id,
            typeName,
            data: { product, simpleProduct },
            resources,
            initialBreakoffEnabled,
            historicalPrice
        } = this.props;

        if (!context.request.user.isAuthenticated) {
            return undefined;
        }

        if (product.PriceFailed) {
            return <span className={`${className}__price__error`}>{resources.priceErrorText || 'Pris n/a'}</span>;
        }

        if (!price || !simpleProduct) {
            return <span className={`${className}__price__retrieving`}>-</span>;
        }

        let productPrice: ProductPrice | undefined;

        if (historicalPrice) {
            // If price is historical we only wanna show the price as is
            productPrice = {
                CustomerContextualPrice: price
            };
        } else {
            // If not use latest price if available otherwise whatever is set on the product
            productPrice = product.ProductPrice ? product.ProductPrice : {
                CustomerContextualPrice: price,
                BasePrice: product.BasePrice
            };
        }

        if (!historicalPrice && !initialBreakoffEnabled && simpleProduct.Behavior?.MultipleOfQuantity) {
            productPrice = multiplyPriceBy(productPrice, simpleProduct.Behavior.MultipleOfQuantity);
        }

        return (
            <PriceComponent
                context={context}
                data={{ price: productPrice }}
                id={id}
                typeName={typeName}
                discountPercentageSide='right' />
        );
    };

    private _renderTotalPrice = (className: string, price: number | undefined): React.ReactNode => {
        const totalPrice: ProductPrice = {
            CustomerContextualPrice: (price || 0) * this.state.orderQuantity
        };

        const hasMixAndMatchDiscount = this.props.data.product.ProductPrice?.DiscountLines?.some(discount => discount.PeriodicDiscountTypeValue === PeriodicDiscountOfferType.MixAndMatch);

        if (hasMixAndMatchDiscount && this.props.data.product.ProductPrice && 'NetPrice' in this.props.data.product.ProductPrice) {
            totalPrice.CustomerContextualPrice = (this.props.data.product.ProductPrice as any).TotalAmount as number;
            totalPrice.BasePrice = (this.props.data.product.ProductPrice as any).NetPrice as number;
            totalPrice.DiscountLines = (this.props.data.product.ProductPrice as any).DiscountLines;
        }

        return (
            <div className={`${className}__total-price-wrapper`}>
                <div className={`${className}__total-price__label`} data-failed={this.props.data.product.PriceFailed}>
                    Totalt:
                </div>
                <div className={`${className}__total-price`} data-failed={this.props.data.product.PriceFailed}>
                    <PriceComponent
                        context={this.props.context}
                        data={{ price: totalPrice }}
                        id={this.props.id}
                        typeName={this.props.typeName} />
                    {hasMixAndMatchDiscount && (
                        <div>
                            <span className={`${className}__total-price__mix-and-match-discount`}>
                                {/* this.props.resources.mixAndMatchDiscountText || */ 'Mix & Match'}
                            </span>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    private _renderDepositPrice = (price: number | undefined): React.ReactNode => {
        const {
            context,
            className,
            id,
            typeName,
            data: { simpleProduct },
            resources
        } = this.props;

        if (!context.app.config?.enableDepositPriceDisplay) {
            return null;
        }

        const deposit = simpleProduct?.ExtensionProperties?.find(x => x.Key === 'DEPOSIT')?.Value?.DecimalValue;

        if (!price || !deposit) {
            return null;
        }

        return (
            <div className={`${className}__price__extra`}>
                +
                &nbsp;
                <PriceComponent
                    context={context}
                    data={{ price: { CustomerContextualPrice: deposit } }}
                    id={id}
                    typeName={typeName} />
                &nbsp;
                {resources.depositSuffixText || 'pant'}
            </div>
        );
    };

    private _renderOrderTemplateIndicator = (baseClassName: string): JSX.Element | undefined => {
        let orderTemplateIndicator: JSX.Element | undefined;
        const { context, data: { product } } = this.props;

        if (!context.request.user.isAuthenticated) {
            return undefined;
        }

        const orderTemplateIndicatorRef = React.createRef<HTMLDivElement>();

        if (product.PartOfProductLists && product.PartOfProductLists.length > 0) {
            orderTemplateIndicator = (
                <div style={{ display: 'inline-block' }}>
                    <div
                        ref={orderTemplateIndicatorRef}
                        className={`${baseClassName}__badge order-template-indicator`}
                    >
                        P
                    </div>
                    <UncontrolledTooltip className={`${baseClassName}__order-template-indicator__tooltip`} placement='bottom-start' trigger='hover focus' target={orderTemplateIndicatorRef}>
                        <div>
                            {/* inOrderTemplateTooltipHeader ||  */'Finnes i plankjøp:'}
                        </div>
                        {product.PartOfProductLists && product.PartOfProductLists.length > 0 && (
                            <ul>
                                {product.PartOfProductLists.map((productList: IProductListByProductId) => {
                                    let url = getUrlSync('ordertemplate', context.actionContext) ?? '';
                                    const separator = url.includes('?') ? '&' : '?';

                                    if (productList.ProductListId) {
                                        url = `${url}${separator}id=${productList.ProductListId}`;
                                    }

                                    return (
                                        <li key={productList.ProductListId}>
                                            <a href={url}>{productList.Name}</a>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </UncontrolledTooltip>
                </div>
            );
        }

        return orderTemplateIndicator;
    };

    private _renderCartCount = (baseClassName: string): React.ReactNode => {
        const { cart, product } = this.props.data;
        // const cartQuantity = cart?.cart.CartLines?.find(x => x.ProductId === product.RecordId)?.Quantity;
        const cartQuantity = cart?.cart.CartLines?.filter(x => x.ProductId === product.RecordId).reduce((acc, val) => acc + (val.Quantity || 0), 0);

        if (!cartQuantity) {
            return undefined;
        }

        const cartCountId = `cart-count-${product.RecordId}`;
        const humanizedUOM = humanizeUnitOfMeasure(this.props.context, product.DefaultUnitOfMeasure).toLowerCase();
        const tooltipString = this.props.resources.inCartTooltipText || '{0} {1} i handlekurven';
        const tooltipText = format(tooltipString, cartQuantity, humanizedUOM);

        return (
            <div
                id={cartCountId}
                className={`${baseClassName}__badge cart-count`}
                onMouseOver={() => this.setState({ showCartCountTooltip: true })}
                onMouseOut={() => this.setState({ showCartCountTooltip: false })}
                onTouchStart={() => this.setState({ showCartCountTooltip: true })}
            >
                {cartQuantity}
                <Tooltip
                    isOpen={this.state.showCartCountTooltip}
                    target={`#${cartCountId}`}
                    className='msc-cart-count-tooltip'
                >
                    {tooltipText}
                </Tooltip>
            </div>
        );
    };

    private _getAvailabilityClassName = (): 'hidden' | 'retrieving' | 'available' | 'error' | 'not-available' => {
        const { product } = this.props.data;
        const { showAvailability } = this.props;

        if (!showAvailability) {
            return 'hidden';
        }

        if (!product.Availability) {
            return 'retrieving';
        }

        if (product.Availability.StockLevelLabel?.startsWith('!')) {
            return 'available';
        }

        if (product.Availability.IsProductAvailable) {
            return 'available';
        }

        if (product.Availability.StockLevelCode === 'ERR') {
            return 'error';
        }

        return 'not-available';
    };

    private _getAvailabilityIcon = (): string | undefined => {
        const { product } = this.props.data;
        const { showAvailability } = this.props;

        if (!showAvailability) {
            return;
        }

        if (!product.Availability) {
            return;
        }

        if (product.Availability.StockLevelLabel?.startsWith('!')) {
            return getAsset('icons/availability-available.svg', this.props.context.request);
        }

        if (product.Availability.IsProductAvailable) {
            return getAsset('icons/availability-available.svg', this.props.context.request);
        }

        if (product.Availability.StockLevelCode === 'ERR') {
            return getAsset('icons/availability-expired.svg', this.props.context.request);
        }

        return getAsset('icons/availability-special-order.svg', this.props.context.request);
    };

    private _renderAvailability = (): React.ReactNode => {
        const { className, data: { product }, showAvailability } = this.props;

        if (!showAvailability) {
            return;
        }

        const availabilityClass = this._getAvailabilityClassName();
        const availabilityIcon =  this._getAvailabilityIcon();
        const stockLevelLabel = product.Availability?.StockLevelLabel?.startsWith('!')
            ? product.Availability?.StockLevelLabel?.substring(1)
            : product.Availability?.StockLevelLabel;
        const availabilityText = stockLevelLabel || (availabilityClass === 'error' ? 'ERROR' : '');

        return (
            <div className={`${className}__product__availability__wrapper`}>
                <div className={classnames(`${className}__product__availability`, availabilityClass)}>
                    <img className={`${className}__product__availability__icon`} src={availabilityIcon} />
                    <span>
                        &nbsp;
                        {availabilityText}
                    </span>
                </div>
            </div>
        );
    };

    private _getOrderTemplateBuyButton = (): React.ReactNode => {
        const { working } = this.state;
        let text = this.locale.cartButton.buy;
        const disabled = this.props.disableBuyButton || working || this.state.orderQuantity < 1;
        const clickHandler = async () => {
            await this._addItemToCart(undefined, true);
        };
        const product = this.props.data.simpleProduct;
        const isExpired = product?.Behavior?.IsSaleAtPhysicalStoresAllowed === false || product?.Behavior?.IsSaleAtSelfCheckoutRegistersAllowed === false;

        if (isExpired) {
            text = this.locale.cartButton.expired;
        }

        return (
            <Button aria-label={text} className='btn-cart-button' title={text} disabled={disabled} onClick={clickHandler}>
                {text}
            </Button>
        );
    };

    private _getCartButton = (): React.ReactNode => {
        const { data: { product, simpleProduct }, cartQueue, disableBuyButton } = this.props;
        const { orderQuantity, working } = this.state;

        const className = classnames('btn-cart-button', { loading: working });
        const disabled = disableBuyButton || working;
        const clickHandler = (action: 'buy' | 'buy_always' | 'remove' | 'update') => {
            return async () => {
                this.setState({ working: true });
                await cartQueue.add(async () => {
                    switch (action) {
                        case 'buy':
                            await this._addItemToCart(undefined);
                            break;
                        case 'buy_always':
                            await this._addItemToCartAlways();
                            break;
                        case 'remove':
                            await this._removeItemFromCart();
                            break;
                        case 'update':
                            await this._addItemToCart(product.CartLine?.LineId);
                            break;
                    }
                });
                this.setState({ working: false });
            };
        };
        const baseProps: ButtonProps = {
            className,
            disabled
        };
        const isExpired = simpleProduct?.Behavior?.IsSaleAtPhysicalStoresAllowed === false || simpleProduct?.Behavior?.IsSaleAtSelfCheckoutRegistersAllowed === false;

        if (isExpired) {
            const text = this.locale.cartButton.expired;
            return (
                <Button {...baseProps} aria-label={text} onClick={clickHandler('buy')} title={text} disabled={true}>
                    {text}
                </Button>
            );
        }

        if (this.props.buyAlwaysAdd) {
            const text = this.locale.cartButton.buy;
            return (
                <Button {...baseProps} aria-label={text} onClick={clickHandler('buy_always')} title={text}>
                    {text}
                </Button>
            );
        } else {
            if (!product.CartLine || !product.CartLine.Quantity) {
                const text = this.locale.cartButton.buy;
                return (
                    <Button {...baseProps} aria-label={text} onClick={clickHandler('buy')} title={text}>
                        {text}
                    </Button>
                );
            }

            if (product.CartLine.Quantity > 0 && orderQuantity < 1) {
                const text = this.locale.cartButton.remove;
                return (
                    <Button {...baseProps} aria-label={text} onClick={clickHandler('remove')} title={text}>
                        {text}
                    </Button>
                );
            }

            if (product.CartLine.Quantity !== orderQuantity) {
                const text = this.locale.cartButton.update;
                return (
                    <Button {...baseProps} aria-label={text} onClick={clickHandler('update')} title={text}>
                        {text}
                    </Button>
                );
            }
        }

        return (
            <Button {...baseProps} aria-label={this.locale.cartButton.disabledTooltip} title={this.locale.cartButton.disabledTooltip}>
                {this.locale.cartButton.buy}
            </Button>
        );
    };

    private readonly _getSearchResultOrderQuantity = (readOnly?: boolean): React.ReactNode => {
        const { product, simpleProduct } = this.props.data;

        if (readOnly) {
            return (
                <div className='quantity readonly'>
                    <span className='quantity__quantity'>{this.state.orderQuantity}</span>
                    &nbsp;
                    <UnitOfMeasure context={this.props.context} simpleProduct={simpleProduct} />
                </div>
            );
        }

        return (
            <QuantityBreakoffSelector
                context={this.props.context}
                disable={this.props.disableQuantityInput}
                product={this.props.data.simpleProduct}
                quantity={this.state.orderQuantity}
                breakoff={Boolean(this.props.initialBreakoffEnabled)}
                unitConversions={this.props.data.unitConversions}
                onBreakoffChange={(breakoff) => {
                    this.props.onBreakoffChange?.(product, breakoff);
                }}
                onQuantityChange={this._onUpdateQuantity}
            />
        );

        /* return (
            <ProductListOrderQuantity
                context={this.props.context}
                allowZero={this.props.moduleType !== 'dlvry-cart'}
                product={this.props.data.simpleProduct
                    ? {
                        ...this.props.data.simpleProduct,
                        DefaultUnitOfMeasure: simpleProduct?.DefaultUnitOfMeasure || product.DefaultUnitOfMeasure
                    }
                    : undefined
                }
                unitConversions={this.props.data.unitConversions}
                locale={this.props.context.request.locale}
                currentCount={this.state.orderQuantity}
                onChange={this._onUpdateQuantity}
                breakoff={this.props.initialBreakoffEnabled}
                onBreakoffChange={enabled => this.props.onBreakoffChange?.(this.props.data.product, enabled)}
                inputQuantityAriaLabel=''
                incrementButtonAriaLabel=''
                decrementButtonAriaLabel=''
                disabled={this.props.disableQuantityInput ||  !this.props.data.simpleProduct}
                min={this.props.min || 0}
            />
        ); */
    };

    private readonly _onUpdateQuantity = (newQuantity: number) => {
        const {
            cartViewProps,
            moduleType,
            selectOnQuantityChange,
            isSelected,
            onSelectCartHandler,
            onUpdateQuantity,
            data: {
                product
            }
        } = this.props;

        this.setState({ orderQuantity: newQuantity });

        if (cartViewProps && cartViewProps.cartlines && moduleType === 'dlvry-cart') {
            const line: any = cartViewProps.cartlines.find(cartLine => cartLine?.data?.cartline?.LineId === product.CartLine?.LineId);
            if (line && line.cartline && line.cartline.props.quantityOnChange) {
                onUpdateQuantity?.(newQuantity);
                return line.cartline.props.quantityOnChange(line.data.cartline, newQuantity);
            }
        }

        if (selectOnQuantityChange) {
            if (this.props.buyAlwaysAdd && newQuantity === 0) {
                onSelectCartHandler?.();
            } else if (!this.props.buyAlwaysAdd && (product.CurrentQuantity || 0) === newQuantity && isSelected) {
                onSelectCartHandler?.();
            } else if (!isSelected) {
                onSelectCartHandler?.();
            }
        }
        onUpdateQuantity?.(newQuantity);
    };

    private readonly _renderProductImage = (
        className: string,
        context: ICoreContext,
        heading: string,
        image?: string,
        fallbackImage?: string,
        imageSettings?: IImageSettings
    ): JSX.Element | null => {
        const width = 580;
        const height = 400;
        const defaultImageSettings: IImageSettings = {
            viewports: {
                xs: { w: width, h: height, q: `w=${width}px&h=${height}&q=80&m=0&f=webp`, qe: `t=${width}h${height}qhigh` },
                lg: { w: width, h: height, q: `w=${width}px&h=${height}&q=80&m=0&f=webp`, qe: `t=${width}h${height}qhigh` },
                xl: { w: width, h: height, q: `w=${width}px&h=${height}&q=80&m=0&f=webp`, qe: `t=${width}h${height}qhigh` }
            },
            disableLazyLoad: !Boolean(imageSettings?.disableLazyLoad),
            lazyload: Boolean(imageSettings?.lazyload),
            quality: imageSettings?.quality || 80
        };

        image = ensureBaseImageUrl(context, image);
        fallbackImage = ensureBaseImageUrl(context, fallbackImage);

        const contextConfig = context.request.app.config?.placeholderImageName;
        const emptyPlaceHolderImage = contextConfig as string;
        let fallbackImageSource = context?.request.apiSettings.baseImageUrl;
        if (fallbackImageSource?.endsWith('/')) {
            fallbackImageSource = fallbackImageSource.slice(0, -1);
        }
        if (emptyPlaceHolderImage) {
            fallbackImageSource = `${fallbackImageSource}${emptyPlaceHolderImage}`;
        }

        return (
            <Image
                requestContext={context.actionContext.requestContext}
                className={className}
                altText={heading}
                title={heading}
                src={image || fallbackImageSource}
                fallBackSrc={fallbackImageSource}
                gridSettings={context.request.gridSettings!}
                imageSettings={defaultImageSettings}
                loadFailureBehavior='empty'
                bypassHideOnFailure
                fetchPriority={ImageFetchPriority.High}
                tabIndex={0}
            />
        );
    };

    // @ts-ignore
    private readonly _getAddToCartActionButton = (className: string, buttonText: string, clickHandler: (event: React.MouseEvent<HTMLElement>) => void): React.ReactNode => {
        return (
            <Button aria-label={buttonText} className={className} onClick={clickHandler} title={buttonText}>
                <span className={`${className}__text`}>{buttonText}</span>
                <span className='fa-regular fa-cart-shopping' />
            </Button>
        );
    };

    private readonly _getRemoveActionButton = (className: string, buttonText: string, clickHandler: (event: React.MouseEvent<HTMLElement>) => void): React.ReactNode => {
        return (
            <Button aria-label={buttonText} className={className} onClick={clickHandler} title={buttonText}>
                <span className='fa-regular fa-xmark no-margin' />
            </Button>
        );
    };

    private readonly _getWishlistActionButton = (): React.ReactNode => {
        const { resources } = this.props;
        const { product, wishlists } = this.props.data;
        const isWishlisted = (wishlists?.length || 0) > 0 && wishlists![0].CommerceListLines?.some(x => x.ProductId === product.RecordId);
        const buttonText = isWishlisted
            ? resources.removeFromWishlistText || 'Fjern fra huskelisten'
            : resources.addToWishlistText || 'Legg til i huskelisten';
        const onClick = isWishlisted ? this.removeItemFromWishlist : this.moveItemToWishlist;

        return (
            <Button
                className={classnames('btn-add-to-wishlist', { 'wishlisted': isWishlisted })}
                title={buttonText}
                aria-label={buttonText}
                onClick={onClick} />
        );
    };

    private readonly _removeItem = async (): Promise<void> => {
        await this.props.onProductRemove?.(this.props.data.product);
    };

    private readonly moveItemToWishlist = async(): Promise<void> => {
        const { context } = this.props;
        const { product } = this.props.data;

        if (!context || !product || !context.request.user.catalogId) {
            return Promise.resolve();
        }

        const productInputs: ProductInput[] = [];
        const productInput = new ProductInput(
            product.RecordId,
            context.request.apiSettings,
            undefined, undefined,
            context.request,
            +context.request.user.catalogId
        );
        productInputs.push(productInput);

        if (context.request.user.isAuthenticated) {
            const customer = await this.getCustomerAccount();

            let wishlists = this.props.data.wishlists;

            if (!wishlists) {
                wishlists = await getWishlistByCustomerId(createGetByCustomerInput({}, null), context.actionContext);
            }

            if (!wishlists || wishlists.length === 0) {
                const wishlistName = this.props.defaultWishlistName || 'My wishlist';
                if (customer && customer.AccountNumber) {
                    const listData: CommerceList = {
                        CustomerId: customer.AccountNumber,
                        Name: wishlistName,
                        CommerceListTypeValue: 1,
                        Id: 0
                    };
                    try {
                        const commerceList = await createCommerceListAsync({
                                callerContext: this.props.context.actionContext
                            },
                            listData);
                        await this.callAddWishlistLine(product.RecordId, commerceList.Id);
                    } catch (err) {
                        const error = err as unknown as any;
                        const footer = error?.data?.CorrelationId ? `Correlation Id: ${error?.data?.CorrelationId}` : undefined;
                        toasts.error(this.locale.toastStrings.errorHeader, error, { footer });
                    }
                }
            } else {
                await this.callAddWishlistLine(product.RecordId, wishlists[0].Id);

                toast({
                    text: `${product.Name} lagt i huskelisten`,
                    type: 'success',
                    autoClose: 3000,
                    position: 'bottom-right',
                    canClose: true,
                    showProgress: false
                });
            }
        }
    };

    private readonly removeItemFromWishlist = async(): Promise<void> => {
        const { context } = this.props;
        const { product } = this.props.data;

        if (!context || !product || !context.request.user.catalogId || !context.request.user.isAuthenticated) {
            return Promise.resolve();
        }

        let wishlists = this.props.data.wishlists;

        if (!wishlists) {
            wishlists = await getWishlistByCustomerId(createGetByCustomerInput({}, null), context.actionContext);
        }

        if (wishlists.length > 0) {
            const commerceLineToRemove = wishlists[0].CommerceListLines?.find(x => x.ProductId === product.RecordId);
            const updatedCommerceList = await removeLinesAsync({ callerContext: context.actionContext },
                wishlists[0].Id,
                [commerceLineToRemove!]);
            context.actionContext.update(createGetByCustomerInput({}, null), [updatedCommerceList]);

            toast({
                text: `${product.Name} fjernet fra huskelisten`,
                type: "success",
                autoClose: 3000,
                position: "bottom-right",
                canClose: true,
                showProgress: false
            });
        }
    };

    private readonly callAddWishlistLine = async (productId: number, wishlistId: number): Promise<void> => {
        const { context } = this.props;

        try {
            const customer = await this.getCustomerAccount();
            if (customer && customer.AccountNumber) {
                const createdCommerceList = await addLinesAsync({ callerContext: context.actionContext },
                    wishlistId,
                    [
                        buildWishlistLine(productId, customer.AccountNumber, wishlistId)
                    ]);
                context.actionContext.update(createGetByCustomerInput({}, null), [createdCommerceList]);
            }
        } catch (err) {
            const error = err as unknown as any;
            const footer = error?.data?.CorrelationId ? `Correlation Id: ${error?.data?.CorrelationId}` : undefined;
            toasts.error(this.locale.toastStrings.errorHeader, error, { footer });
        }
    };

    private readonly getCustomerAccount = (): AsyncResult<Customer> => {
        const customerInput = new GetCustomerInput(this.props.context.actionContext.requestContext.apiSettings);
        return getCustomer(customerInput, this.props.context.actionContext);
    };

    private readonly _addItemToCartAlways = async (): Promise<void> => {
        const { context } = this.props;
        const { product, simpleProduct } = this.props.data;

        if (!context || !product || !simpleProduct || !context.request.user.catalogId) {
            return Promise.resolve();
        }

        let cart = await getCartStateData(new GetCartStateDataInput(context.request.apiSettings, context.request), context.actionContext);

        if (!cart) {
            return;
        }

        while (cart.status === 'UPDATING') {
            console.log('Cart is updating, waiting for it to finish...');
            await new Promise((resolve) => setTimeout(resolve, 1000));
            cart = await getCartStateData(new GetCartStateDataInput(context.request.apiSettings, context.request), context.actionContext);

            if (!cart) {
                // Shouldn't happen, but just in case
                toast({
                    header: 'Error',
                    text: 'Cart is undefined. Please try again later.',
                    autoClose: 30000,
                    canClose: true,
                    position: 'bottom-right',
                    showProgress: true
                });
                return Promise.resolve();
            }
        }

        let userHasAccess: boolean | undefined = this.props.userHasAccess || true; // TODO: Remove 'true' default here to re-enable alcohol license check
        let validationError: string | undefined;

        if (userHasAccess === undefined) {
            const result = await validateAlcoholLicenseAsync(context.actionContext, {
                CatalogId: context.request.apiSettings.catalogId,
                ProductIds: [simpleProduct.RecordId]
            });
            userHasAccess = result.ValidationResult;
            validationError = this.locale.toastStrings.noAccessToProduct;
        }

        const quantity = this.state.orderQuantity || getDefaultBuyQuantity(simpleProduct, !!this.props.initialBreakoffEnabled);

        if (userHasAccess && this.props.onBuyValidate) {
            const result = await this.props.onBuyValidate(
                product,
                quantity
            );
            userHasAccess = result.isValid;
            validationError = result.message;
        }

        if (userHasAccess !== true) {
            toasts.error(this.locale.toastStrings.errorHeader, validationError || 'Unknown error');
            return Promise.resolve();
        }

        const addProductToCartInput = {
            product: simpleProduct,
            count: quantity,
            customPrice: product.Price
        };

        const cartLine = cart.cart.CartLines?.find(x => x.ProductId === simpleProduct.RecordId);

        try {
            const toAddCount = (!cartLine || !cartLine.LineId)
                ? addProductToCartInput.count
                : quantity;
            const result = (!cartLine || !cartLine.LineId)
                ? await cart.addProductToCart(addProductToCartInput)
                : await cart.updateCartLineQuantity({
                    cartLineId: cartLine.LineId,
                    newQuantity: (cartLine.Quantity || 0) + toAddCount
                });

            if (result.status === 'SUCCESS') {
                void GTM.addToCartAsync(product, toAddCount);

                const msg: IToastViewProps = {
                    text: format(this.locale.toastStrings.productsAddedToCart, toAddCount, simpleProduct.Name ?? ''),
                    type: 'success',
                    autoClose: 3000,
                    position: 'bottom-right',
                    canClose: true,
                    showProgress: true,
                    link: this.cartUrl,
                    linkLabel: this.locale.toastStrings.goToCart
                };
                toast(msg);

                if (this.props.isSelected) {
                    this.props.onSelectCartHandler?.();
                }
                this.setState({ orderQuantity: 0 });
            }
            else if (result.status === 'FAILED') {
                const text = result.validationResults && result.validationResults.length > 0
                    ? result.validationResults[0].LocalizedMessage!
                    : (result.errorDetails?.LocalizedMessage ?? 'Unknown error adding items to cart');
                toasts.error(this.locale.toastStrings.errorHeader, text);
            }
        }
        catch (err) {
            const error = err as unknown as any;
            const footer = error?.data?.CorrelationId ? `Correlation Id: ${error?.data?.CorrelationId}` : undefined;
            toasts.error(this.locale.toastStrings.errorHeader, error, { footer });
        }
    };

    private readonly _addItemToCart = async (cartLineId: string | undefined, resetQuantiyOnSuccess?: boolean): Promise<void> => {
        const { context } = this.props;
        const { product, simpleProduct } = this.props.data;

        if (!context || !product || !simpleProduct || !context.request.user.catalogId) {
            return Promise.resolve();
        }

        let cart = await getCartStateData(new GetCartStateDataInput(context.request.apiSettings, context.request), context.actionContext);

        if (cart) {
            while (cart.status === 'UPDATING') {
                console.log('Cart is updating, waiting for it to finish...');
                await new Promise((resolve) => setTimeout(resolve, 1000));
                cart = await getCartStateData(new GetCartStateDataInput(context.request.apiSettings, context.request), context.actionContext);

                if (!cart) {
                    // Shouldn't happen, but just in case
                    toast({
                        header: 'Error',
                        text: 'Cart is undefined. Please try again later.',
                        autoClose: 30000,
                        canClose: true,
                        position: 'bottom-right',
                        showProgress: true
                    });
                    return Promise.resolve();
                }
            }

            let userHasAccess: boolean | undefined = this.props.userHasAccess || true; // TODO: Remove 'true' default here to re-enable alcohol license check
            let validationError: string | undefined;

            if (userHasAccess === undefined) {
                const result = await validateAlcoholLicenseAsync(context.actionContext, {
                    CatalogId: context.request.apiSettings.catalogId,
                    ProductIds: [simpleProduct.RecordId]
                });
                userHasAccess = result.ValidationResult;
                validationError = this.locale.toastStrings.noAccessToProduct;
            }

            const quantity = this.state.orderQuantity || getDefaultBuyQuantity(simpleProduct, !!this.props.initialBreakoffEnabled);

            if (userHasAccess && this.props.onBuyValidate) {
                const result = await this.props.onBuyValidate(
                    product,
                    quantity
                );
                userHasAccess = result.isValid;
                validationError = result.message;
            }

            if (userHasAccess !== true) {
                toasts.error(this.locale.toastStrings.errorHeader, validationError || 'Unknown error');
                return Promise.resolve();
            }

            /* if (!cartLineId) {
                cartLineId = cart.cart.CartLines?.find(x => x.ProductId === simpleProduct.RecordId)?.LineId;
            }
            const cartQuantity = cartLineId ? cart.cart.CartLines?.find(x => x.LineId === cartLineId)?.Quantity || 0 : 0; */

            const addProductToCartInput = {
                product: simpleProduct,
                count: quantity,
                /* isPriceKeyedIn: true, */
                customPrice: product.Price
            };

            try {
                const result = !cartLineId
                    ? await cart.addProductToCart(addProductToCartInput)
                    : await cart.updateCartLineQuantity({ cartLineId, newQuantity: this.state.orderQuantity });
                if (result.status === "SUCCESS") {
                    if (cartLineId) {
                        const cartLine = cart.cart.CartLines?.find(x => x.LineId === cartLineId)!;
                        // Check if new quantity is lower or higher than cart quantity
                        const higher = quantity && cartLine.Quantity && quantity > cartLine.Quantity;
                        const difference = quantity && cartLine.Quantity ? Math.abs(quantity - cartLine.Quantity) : 0;

                        if (higher) {
                            void GTM.addToCartAsync(product, difference);
                        } else {
                            void GTM.removeFromCartAsync(product, difference);
                        }
                    } else {
                        void GTM.addToCartAsync(product, quantity);
                    }

                    const msg: IToastViewProps = {
                        text: this.locale.toastStrings.productsAddedToCart
                            .replace('{0}', quantity.toString())
                            .replace('{1}', simpleProduct.Name ?? ''),
                        type: "success",
                        autoClose: 3000,
                        position: "bottom-right",
                        canClose: true,
                        showProgress: true,
                        link: this.cartUrl,
                        linkLabel: this.locale.toastStrings.goToCart
                    };
                    toast(msg);

                    if (resetQuantiyOnSuccess) {
                        // Reset quantity to 0 after successfully adding to cart
                        this.setState({ orderQuantity: 0 });
                        this.props.onUpdateQuantity?.(0);
                    } else if (this.state.orderQuantity === 0) {
                        // Make sure product has at least 1 quantity after being successfully added to cart
                        this.setState({ orderQuantity: 1 });
                        this.props.onUpdateQuantity?.(1);
                    }
                }
                else if (result.status === "FAILED") {
                    const text = result.validationResults && result.validationResults.length > 0
                        ? result.validationResults[0].LocalizedMessage!
                        : (result.errorDetails?.LocalizedMessage ?? 'Unknown error adding items to cart');
                    toasts.error(this.locale.toastStrings.errorHeader, text);
                }
            }
            catch (err) {
                const error = err as unknown as any;
                const footer = error?.data?.CorrelationId ? `Correlation Id: ${error?.data?.CorrelationId}` : undefined;
                toasts.error(this.locale.toastStrings.errorHeader, error, { footer });
            }
        }
    };

    private readonly _removeItemFromCart = async (): Promise<void> => {
        const { context } = this.props;
        const { product } = this.props.data;

        if (!product.CartLine || !product.CartLine.LineId) {
            return Promise.resolve();
        }

        const cart = await getCartStateData(new GetCartStateDataInput(context.request.apiSettings, context.request), context.actionContext);

        if (!cart) {
            toasts.error(this.locale.toastStrings.errorHeader, this.locale.toastStrings.errorCartNotFound);
            return Promise.resolve();
        }

        const result = await cart.removeCartLines({
            cartLineIds: [product.CartLine.LineId]
        });

        if (result.status === "SUCCESS") {
            void GTM.removeFromCartAsync(product, product.CartLine.Quantity || 1);

            toast({
                text: this.locale.toastStrings.productsRemovedFromCart.replace('{0}', product.Name ?? ''),
                type: "success",
                autoClose: 3000,
                position: "bottom-right",
                canClose: true,
                showProgress: true,
                link: this.cartUrl,
                linkLabel: this.locale.toastStrings.goToCart
            });
        } else if (result.status === "FAILED") {
            const text = result.validationResults && result.validationResults.length > 0
                ? result.validationResults[0].LocalizedMessage!
                : (result.errorDetails?.LocalizedMessage ?? 'Unknown error removing items from cart');
            toasts.error(this.locale.toastStrings.errorHeader, text);
        }
    };
}
